import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Copy, CheckCircle2, X, QrCode, Loader2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { Button } from '../common/Button';
import { useAuth } from '../../context/AuthContext';

// Checkout Pix (fase 2 — MercadoPago): cria pagamento, mostra QR/copiar-código e
// polla o status até aprovar/cancelar/30min. O preço vem SEMPRE do server.
type PixData = { qrCode?: string; qrCodeBase64?: string; ticketUrl?: string };

type CheckoutResp = {
  payment: {
    id: string;
    amount: number;
    credits: number;
    status: string;
    productId: string;
    productName: string;
  };
  pix: PixData;
};

type StatusResp = { status: string; credits?: number; amount?: number; balance?: number | null };

const POLL_MS = 4000;
const MAX_MINUTES = 30;

export const PixCheckoutModal: React.FC<{
  productId: string | null;
  onClose: () => void;
}> = ({ productId, onClose }) => {
  const { setCredits } = useAuth();
  const [data, setData] = useState<CheckoutResp | null>(null);
  const [status, setStatus] = useState<string>('pending');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const deadline = useRef(0);

  useEffect(() => {
    if (!productId) return;
    let alive = true;
    setData(null);
    setStatus('pending');
    setError(null);
    setLoading(true);

    api
      .post<CheckoutResp>('/payments/checkout', { productId })
      .then((resp) => {
        if (!alive) return;
        setData(resp);
        deadline.current = Date.now() + MAX_MINUTES * 60_000;
      })
      .catch((err) => {
        if (!alive) return;
        setError(pixErrorMessage(err));
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [productId]);

  useEffect(() => {
    if (!data || status !== 'pending') return;
    const tick = async () => {
      if (Date.now() > deadline.current) {
        setStatus('expired');
        return;
      }
      try {
        const s = await api.get<StatusResp>(`/payments/${data.payment.id}/status`);
        if (Date.now() > deadline.current) {
          setStatus('expired');
          return;
        }
        if (s.status === 'approved') {
          if (typeof s.balance === 'number') setCredits(s.balance);
          setStatus('approved');
        } else if (s.status === 'cancelled') {
          setStatus('cancelled');
        }
      } catch {
        // falha pontual do poll — próxima tentativa
      }
    };
    const id = setInterval(tick, POLL_MS);
    return () => clearInterval(id);
  }, [data, status, setCredits]);

  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

  const copyCode = async () => {
    if (!data?.pix.qrCode) return;
    try {
      await navigator.clipboard.writeText(data.pix.qrCode);
      setCopied(true);
      toast.success('Código Pix copiado');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Não consegui copiar — selecione o código manualmente');
    }
  };

  if (!productId) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-indigo-950 rounded-lg p-6 w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-purple-200 hover:text-purple-100"
          aria-label="Fechar"
        >
          <X className="w-6 h-6" />
        </button>

        <h3 className="text-xl font-bold text-purple-200 mb-1">Pagamento por Pix</h3>

        {loading && (
          <div className="flex flex-col items-center gap-3 py-10 text-purple-200/80">
            <Loader2 className="w-8 h-8 animate-spin" />
            <p>Preparando seu Pix…</p>
          </div>
        )}

        {error && (
          <div className="py-8 text-center space-y-4">
            <p className="text-purple-200/90">{error}</p>
            <Button onClick={onClose}>Fechar</Button>
          </div>
        )}

        {data && status === 'pending' && (
          <div className="mt-4 space-y-4">
            <p className="text-purple-300">
              <b className="text-purple-200">{data.payment.productName}</b> · R$ {data.payment.amount.toFixed(2)}
              {data.payment.credits > 0 && (
                <span className="text-purple-300/80"> · {data.payment.credits} créditos</span>
              )}
            </p>
            {data.pix.qrCodeBase64 ? (
              <div className="flex justify-center rounded-lg bg-white p-3">
                <img
                  src={`data:image/png;base64,${data.pix.qrCodeBase64}`}
                  alt="QR Code do Pix"
                  className="w-56 h-56"
                />
              </div>
            ) : (
              <div className="rounded-lg bg-white/5 p-4 text-center">
                <QrCode className="w-10 h-10 mx-auto text-purple-300/50" />
                <p className="mt-2 text-sm text-purple-300/70">QR indisponível — use o código copia-e-cola:</p>
              </div>
            )}
            {data.pix.qrCode && (
              <div className="space-y-2">
                <div className="max-h-24 overflow-y-auto rounded bg-black/30 p-2 text-[11px] break-all text-purple-200/90 font-mono">
                  {data.pix.qrCode}
                </div>
                <Button variant="outline" onClick={copyCode} className="w-full">
                  {copied ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copiado!' : 'Copiar código Pix'}
                </Button>
              </div>
            )}
            <p className="flex items-center gap-2 text-sm text-purple-300/80">
              <Loader2 className="w-4 h-4 animate-spin" /> Aguardando confirmação… (chega em segundos pagando
              pelo app)
            </p>
            {data.pix.ticketUrl && (
              <a
                href={data.pix.ticketUrl}
                target="_blank"
                rel="noreferrer"
                className="block text-center text-sm text-purple-300 underline"
              >
                Ver comprovante no MercadoPago
              </a>
            )}
          </div>
        )}

        {status === 'approved' && (
          <div className="py-8 text-center space-y-4">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400" />
            <p className="text-purple-200 font-semibold">Pagamento confirmado — obrigado!</p>
            {data?.payment.credits ? (
              <p className="text-purple-300/90">Seus {data.payment.credits} créditos já estão na conta.</p>
            ) : (
              <p className="text-purple-300/90">
                Seu pedido foi registrado — nossa equipe entrará em contato pelo seu cadastro.
              </p>
            )}
            <Button onClick={onClose}>Fechar</Button>
          </div>
        )}

        {(status === 'cancelled' || status === 'expired') && (
          <div className="py-8 text-center space-y-4">
            <p className="text-purple-200/90">
              {status === 'cancelled' ? 'Pagamento cancelado/não aprovado.' : 'Tempo do checkout esgotado.'}
            </p>
            <p className="text-purple-300/80 text-sm">Sem cobrança — pode tentar de novo quando quiser.</p>
            <Button onClick={onClose}>Fechar</Button>
          </div>
        )}
      </div>
    </div>
  );
};

function pixErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 503) return 'Pagamento em breve — fale com a gente';
  if (err instanceof ApiError && err.status === 401) return 'Entre na sua conta para comprar';
  return 'Não rolou criar o pagamento agora — tente em instantes';
}